
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { contextSyncService } from '@/services/contextSync';

interface VariantMetrics {
  forecast_velocity: number;
  historical_velocity: number;
  forecast_next_cycle_velocity: number;  // Daily sales rate for coverage period
  current_stock: number;
  stock_days: number;
  incoming_inventory: number;
  suggested_qty: number;
  lead_time: number;
  reorder_cycle: number;
  safety_stock: number;
  reorder_point: number;
  forecast_lead_time_demand: number;
  forecast_next_cycle_demand: number;
  unit_cost: number;
  gross_margin_pct: number;
  coverage_start_date?: string;
  coverage_end_date?: string;
}

interface VariantUrgency {
  days_until_stockout: number;
  days_until_order_arrives: number;
  will_stockout_before_order: boolean;
  stockout_gap_days: number;
  urgency_level: 'ok' | 'warning' | 'critical';
}

interface OrderConstraints {
  case_quantity: number;
  min_order_qty: number;
  max_order_qty: number;
}

interface VariantData {
  product_name: string;
  variant_name: string;
  sku: string;
  variant_id: string;
  metrics: VariantMetrics;
  urgency: VariantUrgency;
  order_constraints: OrderConstraints;
  match_confidence: number;
}

interface RecommendationSummary {
  total_variants: number;
  total_suggested_qty: number;
  urgent_count: number;
  critical_count: number;
  avg_stock_days: number;
  product_name?: string;
  // Aggregated metrics for product-level display
  total_current_stock?: number;
  total_incoming_inventory?: number;
  avg_forecast_velocity?: number;
  avg_historical_velocity?: number;
}

interface RecommendationExplanationParameters {
  match_info: {
    query: string;
    match_type: string;
    confidence: number;
    variant_count: number;
  };
  variants: VariantData[];
  summary: RecommendationSummary;
  primaryVariantId?: string;  // Empty when product-level query (no specific variant)
  requestedSku?: string;      // The SKU that was requested (empty if product-level)
  // Historical date range used for sales analysis
  historical_date_range?: {
    start: string;
    end: string;
  };
  historical_date_range_display?: string;
}

@Component({
  name: 'RecommendationExplanationDisplay'
})
export default class RecommendationExplanationDisplay extends Vue {
  @Prop({ default: null }) readonly chatResponse!: any;
  @Prop({ default: () => ({}) }) readonly actionParameters!: RecommendationExplanationParameters;

  mounted() {
    this.syncScreenContext();
  }

  @Watch('actionParameters', { deep: true })
  onActionParametersChanged() {
    this.syncScreenContext();
  }

  /**
   * Sync screen context to contextSyncService for automatic context attachment.
   * Called on mount and when actionParameters change.
   */
  private syncScreenContext(): void {
    if (!contextSyncService.getSessionId()) {
      return;
    }

    if (!this.hasData) {
      return; // No data to sync
    }

    contextSyncService.updateScreenContext({
      screen_name: 'recommendation_explanation',
      screen_path: '/purchase-assistant',
      screen_params: {
        product_name: this.productName,
        sku: this.sku || undefined,
        variant_name: this.variantName || undefined,
      },
      component_state: {
        screen_type: 'recommendation_explanation',
        screen_data: {
          product_name: this.productName,
          sku: this.sku || undefined,
          variant_name: this.variantName || undefined,
          is_product_level: this.isProductLevel,
          variant_count: this.variantCount,
          // Inventory metrics
          inventory_metrics: {
            current_stock: this.currentStock,
            stock_days: this.stockDays,
            incoming_inventory: this.incomingInventory,
            suggested_qty: this.suggestedQty,
          },
          // Velocity metrics
          velocity_metrics: {
            forecast_velocity: this.forecastVelocity,
            historical_velocity: this.historicalVelocity,
          },
          // Urgency info
          urgency: {
            level: this.urgencyLevel,
            days_until_stockout: this.daysUntilStockout,
            urgent_count: this.urgentCount,
            critical_count: this.criticalCount,
          },
          // Replenishment parameters
          replenishment_params: {
            lead_time: this.leadTime,
            reorder_cycle: this.reorderCycle,
            safety_stock: this.safetyStock,
          },
          // Multi-variant summary if applicable
          variants_summary: this.hasMultipleVariants ? this.variants.map(v => ({
            variant_id: v.variant_id,
            variant_name: v.variant_name,
            sku: v.sku,
            suggested_qty: v.metrics?.suggested_qty ?? 0,
            urgency_level: v.urgency?.urgency_level ?? 'ok',
          })) : undefined,
        },
      },
    });
  }

  // Check if this is a product-level query (no specific variant requested)
  get isProductLevel(): boolean {
    const primaryVariantId = this.actionParameters?.primaryVariantId || '';
    return !primaryVariantId && this.variants.length > 1;
  }

  // Primary variant (first or only variant) - only used when specific variant requested
  get primaryVariant(): VariantData | null {
    if (this.isProductLevel) {
      return null; // No primary variant for product-level queries
    }
    return this.variants?.[0] || null;
  }

  get hasData(): boolean {
    return this.variants.length > 0;
  }

  get variants(): VariantData[] {
    return this.actionParameters?.variants || [];
  }

  get variantCount(): number {
    return this.actionParameters?.summary?.total_variants || this.variants.length;
  }

  get urgentCount(): number {
    return this.actionParameters?.summary?.urgent_count || 0;
  }

  get criticalCount(): number {
    return this.actionParameters?.summary?.critical_count || 0;
  }

  get hasMultipleVariants(): boolean {
    return this.variants.length > 1;
  }

  // Product info - use aggregated data for product-level queries
  get productName(): string {
    return this.actionParameters?.summary?.product_name ||
           this.variants?.[0]?.product_name ||
           'Product';
  }

  get variantName(): string {
    // Don't show variant name for product-level queries
    if (this.isProductLevel) {
      return '';
    }
    return this.primaryVariant?.variant_name || '';
  }

  get sku(): string {
    // Don't show SKU for product-level queries
    if (this.isProductLevel) {
      return '';
    }
    return this.primaryVariant?.sku || '';
  }

  // Metrics - use aggregated summary data for product-level queries
  get metrics(): VariantMetrics | null {
    return this.primaryVariant?.metrics || null;
  }

  get currentStock(): number {
    if (this.isProductLevel) {
      return this.actionParameters?.summary?.total_current_stock ?? 0;
    }
    return this.metrics?.current_stock ?? 0;
  }

  get stockDays(): number {
    if (this.isProductLevel) {
      return this.actionParameters?.summary?.avg_stock_days ?? 0;
    }
    return this.metrics?.stock_days ?? 0;
  }

  get incomingInventory(): number {
    if (this.isProductLevel) {
      return this.actionParameters?.summary?.total_incoming_inventory ?? 0;
    }
    return this.metrics?.incoming_inventory ?? 0;
  }

  get forecastVelocity(): number {
    if (this.isProductLevel) {
      return this.actionParameters?.summary?.avg_forecast_velocity ?? 0;
    }
    return this.metrics?.forecast_velocity ?? 0;
  }

  get historicalVelocity(): number {
    if (this.isProductLevel) {
      return this.actionParameters?.summary?.avg_historical_velocity ?? 0;
    }
    return this.metrics?.historical_velocity ?? 0;
  }

  get suggestedQty(): number {
    if (this.isProductLevel) {
      return this.actionParameters?.summary?.total_suggested_qty ?? 0;
    }
    return this.metrics?.suggested_qty ?? 0;
  }

  get leadTime(): number {
    if (this.isProductLevel) {
      // For product-level, use first variant's lead time (usually same for all variants)
      return this.variants?.[0]?.metrics?.lead_time ?? 0;
    }
    return this.metrics?.lead_time ?? 0;
  }

  get reorderCycle(): number {
    if (this.isProductLevel) {
      return this.variants?.[0]?.metrics?.reorder_cycle ?? 0;
    }
    return this.metrics?.reorder_cycle ?? 0;
  }

  get safetyStock(): number {
    return this.metrics?.safety_stock ?? 0;
  }

  get reorderPoint(): number {
    return this.metrics?.reorder_point ?? 0;
  }

  get forecastLeadTimeDemand(): number {
    return this.metrics?.forecast_lead_time_demand ?? 0;
  }

  get forecastNextCycleDemand(): number {
    return this.metrics?.forecast_next_cycle_demand ?? 0;
  }

  // Total Inventory Need = Coverage Demand + Safety Stock (matches BuyingTable formula)
  get totalInventoryNeed(): number {
    const coverageDemand = this.forecastNextCycleDemand || 0;
    const safetyStock = this.safetyStock || 0;
    return Math.round(coverageDemand + safetyStock);
  }

  get unitCost(): number {
    return this.metrics?.unit_cost ?? 0;
  }

  get grossMarginPct(): number {
    return this.metrics?.gross_margin_pct ?? 0;
  }

  get coverageStartDate(): string {
    return this.metrics?.coverage_start_date || '';
  }

  get coverageEndDate(): string {
    return this.metrics?.coverage_end_date || '';
  }

  // Historical date range for sales analysis context
  get historicalDateRangeDisplay(): string {
    return this.actionParameters?.historical_date_range_display || '';
  }

  get historicalPeriodDescription(): string {
    const range = this.actionParameters?.historical_date_range;
    if (!range?.start || !range?.end) return '';

    const start = new Date(range.start);
    const end = new Date(range.end);
    const days = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

    if (days >= 60) {
      const months = Math.round(days / 30);
      return `${months} month${months > 1 ? 's' : ''}`;
    } else if (days >= 14) {
      const weeks = Math.round(days / 7);
      return `${weeks} week${weeks > 1 ? 's' : ''}`;
    } else {
      return `${days} day${days > 1 ? 's' : ''}`;
    }
  }

  // Urgency - use overall urgency for product-level queries
  get urgency(): VariantUrgency | null {
    return this.primaryVariant?.urgency || null;
  }

  get urgencyLevel(): string {
    if (this.isProductLevel) {
      // For product-level, determine overall urgency from summary
      const summary = this.actionParameters?.summary;
      if (summary?.critical_count && summary.critical_count > 0) {
        return 'critical';
      }
      if (summary?.urgent_count && summary.urgent_count > 0) {
        return 'warning';
      }
      return 'ok';
    }
    return this.urgency?.urgency_level || 'ok';
  }

  get daysUntilStockout(): number {
    if (this.isProductLevel) {
      // For product-level, use average stock days as proxy
      return this.actionParameters?.summary?.avg_stock_days ?? 0;
    }
    return this.urgency?.days_until_stockout ?? 0;
  }

  get stockoutGapDays(): number {
    return this.urgency?.stockout_gap_days ?? 0;
  }

  get urgencyColor(): string {
    switch (this.urgencyLevel) {
      case 'critical': return 'error';
      case 'warning': return 'warning';
      default: return 'success';
    }
  }

  get urgencyIcon(): string {
    switch (this.urgencyLevel) {
      case 'critical': return 'mdi-alert-circle';
      case 'warning': return 'mdi-alert';
      default: return 'mdi-check-circle';
    }
  }

  get urgencyLabel(): string {
    switch (this.urgencyLevel) {
      case 'critical': return 'Critical';
      case 'warning': return 'Low Stock';
      default: return 'OK';
    }
  }

  get stockDaysColor(): string {
    if (this.stockDays < this.leadTime) return 'error';
    if (this.stockDays < this.leadTime * 1.5) return 'warning';
    return 'success';
  }

  // Order constraints
  get orderConstraints(): OrderConstraints | null {
    return this.primaryVariant?.order_constraints || null;
  }

  get caseQuantity(): number {
    return this.orderConstraints?.case_quantity ?? 0;
  }

  get minOrderQty(): number {
    return this.orderConstraints?.min_order_qty ?? 0;
  }

  get maxOrderQty(): number {
    return this.orderConstraints?.max_order_qty ?? 0;
  }

  get hasOrderConstraints(): boolean {
    return this.caseQuantity > 0 || this.minOrderQty > 0 || this.maxOrderQty > 0;
  }

  // Summary data
  get totalSuggestedQty(): number {
    return this.actionParameters?.summary?.total_suggested_qty ?? 0;
  }

  // Helper methods
  getVariantUrgencyColor(variant: VariantData): string {
    switch (variant.urgency?.urgency_level) {
      case 'critical': return 'error';
      case 'warning': return 'warning';
      default: return 'success';
    }
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
      return dateStr;
    }
  }

  formatNumber(value: number, decimals: number = 2): string {
    if (value === null || value === undefined) return '0';
    return Number(value).toFixed(decimals);
  }

  /**
   * Sanitize a number value to prevent negative, NaN, or Infinity from causing calculation errors
   */
  sanitizeNumber(val: any): number {
    const num = Number(val);
    if (isNaN(num) || !isFinite(num) || num < 0) {
      return 0;
    }
    return num;
  }

  /**
   * Compute inventory projections for timeline visualization.
   * Adapted from BuyingTable.vue to work with RecommendationExplanationDisplay data structure.
   */
  get inventoryProjections(): any {
    // Only calculate for single variant view
    if (this.isProductLevel || !this.primaryVariant) return null;

    const metrics = this.primaryVariant.metrics;
    if (!metrics) return null;

    const currentStock = metrics.current_stock || 0;
    const incomingQuantity = metrics.incoming_inventory || 0;
    const safetyStock = metrics.safety_stock || 0;
    const forecastLeadTimeDemand = metrics.forecast_lead_time_demand || 0;
    const forecastNextCycleDemand = metrics.forecast_next_cycle_demand || 0;
    const suggestedQuantity = Math.round(metrics.suggested_qty || 0);
    const vendorLeadTime = metrics.lead_time || 30;
    const coverageDays = metrics.reorder_cycle || vendorLeadTime;

    // Sanitize sales values
    const historicalDailySales = this.sanitizeNumber(metrics.historical_velocity);
    const forecastDailySalesLeadTime = this.sanitizeNumber(metrics.forecast_velocity);
    // Use forecast_next_cycle_velocity for coverage period, fallback to lead time velocity
    const forecastDailySalesCoverage =
      this.sanitizeNumber(metrics.forecast_next_cycle_velocity) || forecastDailySalesLeadTime;

    // Calculate dates
    const today = new Date();

    // Reorder point
    const reorderPoint = metrics.reorder_point || safetyStock;

    // Calculate days to reorder
    let daysToReorderPoint = forecastDailySalesLeadTime > 0
      ? Math.max(0, Math.floor((currentStock - reorderPoint) / forecastDailySalesLeadTime))
      : 0;

    // If current stock is at or below reorder point, order now
    if (currentStock <= reorderPoint) {
      daysToReorderPoint = 0;
    }

    const reorderDate = new Date(today);
    reorderDate.setDate(reorderDate.getDate() + daysToReorderPoint);

    // Projected inventory at reorder date
    const forecastDemandToReorder = daysToReorderPoint * forecastDailySalesLeadTime;
    const inventoryAtReorder = Math.max(0, currentStock - forecastDemandToReorder);

    // Inventory at end of lead time when order arrives
    const inventoryAtArrival = Math.max(0, inventoryAtReorder - forecastLeadTimeDemand);

    // Inventory after restock
    const inventoryAfterRestock = inventoryAtArrival + suggestedQuantity + incomingQuantity;

    // Inventory at end of coverage period
    const inventoryAtNextReorder = Math.max(0, inventoryAfterRestock - forecastNextCycleDemand);

    // Status indicators
    const isAboveSafetyAtArrival = inventoryAtArrival >= safetyStock;
    const isAboveSafetyAtNextReorder = inventoryAtNextReorder >= safetyStock;
    const willStockOutDuringLeadTime = inventoryAtArrival <= 0;

    // Days until stockout
    const daysUntilStockout = currentStock === 0
      ? 0
      : forecastDailySalesLeadTime > 0
        ? Math.floor(currentStock / forecastDailySalesLeadTime)
        : 365;

    // Trend calculations
    const leadTimeTrendPercent = historicalDailySales > 0
      ? Math.round(((forecastDailySalesLeadTime - historicalDailySales) / historicalDailySales) * 100)
      : 0;
    const coverageTrendPercent = historicalDailySales > 0
      ? Math.round(((forecastDailySalesCoverage - historicalDailySales) / historicalDailySales) * 100)
      : 0;

    // Risk level
    const riskLevel = willStockOutDuringLeadTime ? 'critical'
      : !isAboveSafetyAtArrival ? 'warning'
      : 'healthy';

    // Arrival date = reorder date + lead time
    const arrivalDate = new Date(reorderDate);
    arrivalDate.setDate(arrivalDate.getDate() + vendorLeadTime);

    // Next cycle end = arrival date + coverage days
    const nextCycleEnd = new Date(arrivalDate);
    nextCycleEnd.setDate(nextCycleEnd.getDate() + coverageDays);

    // Days from today to reorder
    const daysToReorder = Math.max(0, Math.ceil((reorderDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)));

    return {
      currentStock,
      incomingQuantity,
      safetyStock,
      suggestedQuantity,
      vendorLeadTime,
      coverageDays,
      historicalDailySales,
      forecastDailySalesLeadTime,
      forecastDailySalesCoverage,
      forecastLeadTimeDemand,
      forecastNextCycleDemand,
      inventoryAtArrival,
      inventoryAfterRestock,
      inventoryAtNextReorder,
      isAboveSafetyAtArrival,
      isAboveSafetyAtNextReorder,
      willStockOutDuringLeadTime,
      daysUntilStockout,
      daysRemaining: daysUntilStockout,
      daysRemainingAtReorder: forecastDailySalesLeadTime > 0
        ? Math.floor(inventoryAtReorder / forecastDailySalesLeadTime)
        : 999,
      leadTimeTrendPercent,
      coverageTrendPercent,
      riskLevel,
      daysToReorder,
      reorderPoint,
      inventoryAtReorder,
      forecastDemandToReorder,
      // Formatted dates for display
      todayFormatted: today.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      reorderDateFormatted: reorderDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      arrivalDateFormatted: arrivalDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      nextCycleEndFormatted: nextCycleEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      // Inventory need calculation
      totalInventoryNeed: forecastNextCycleDemand + safetyStock,
      // Raw calculated suggested quantity (before rounding)
      rawSuggestedQuantity: forecastNextCycleDemand + safetyStock - incomingQuantity,
    };
  }

  /**
   * Get inventory projections for a specific variant (used in expandable multi-variant view)
   */
  getVariantInventoryProjections(variant: VariantData): any {
    if (!variant || !variant.metrics) return null;

    const metrics = variant.metrics;
    const currentStock = metrics.current_stock || 0;
    const incomingQuantity = metrics.incoming_inventory || 0;
    const safetyStock = metrics.safety_stock || 0;
    const forecastLeadTimeDemand = metrics.forecast_lead_time_demand || 0;
    const forecastNextCycleDemand = metrics.forecast_next_cycle_demand || 0;
    const suggestedQuantity = Math.round(metrics.suggested_qty || 0);
    const vendorLeadTime = metrics.lead_time || 30;
    const coverageDays = metrics.reorder_cycle || vendorLeadTime;

    const historicalDailySales = this.sanitizeNumber(metrics.historical_velocity);
    const forecastDailySalesLeadTime = this.sanitizeNumber(metrics.forecast_velocity);
    // Use forecast_next_cycle_velocity for coverage period, fallback to lead time velocity
    const forecastDailySalesCoverage =
      this.sanitizeNumber(metrics.forecast_next_cycle_velocity) || forecastDailySalesLeadTime;

    const today = new Date();
    const reorderPoint = metrics.reorder_point || safetyStock;

    let daysToReorderPoint = forecastDailySalesLeadTime > 0
      ? Math.max(0, Math.floor((currentStock - reorderPoint) / forecastDailySalesLeadTime))
      : 0;

    if (currentStock <= reorderPoint) {
      daysToReorderPoint = 0;
    }

    const reorderDate = new Date(today);
    reorderDate.setDate(reorderDate.getDate() + daysToReorderPoint);

    const forecastDemandToReorder = daysToReorderPoint * forecastDailySalesLeadTime;
    const inventoryAtReorder = Math.max(0, currentStock - forecastDemandToReorder);
    const inventoryAtArrival = Math.max(0, inventoryAtReorder - forecastLeadTimeDemand);
    const inventoryAfterRestock = inventoryAtArrival + suggestedQuantity + incomingQuantity;
    const inventoryAtNextReorder = Math.max(0, inventoryAfterRestock - forecastNextCycleDemand);

    const isAboveSafetyAtArrival = inventoryAtArrival >= safetyStock;
    const isAboveSafetyAtNextReorder = inventoryAtNextReorder >= safetyStock;
    const willStockOutDuringLeadTime = inventoryAtArrival <= 0;

    const daysUntilStockout = currentStock === 0
      ? 0
      : forecastDailySalesLeadTime > 0
        ? Math.floor(currentStock / forecastDailySalesLeadTime)
        : 365;

    const leadTimeTrendPercent = historicalDailySales > 0
      ? Math.round(((forecastDailySalesLeadTime - historicalDailySales) / historicalDailySales) * 100)
      : 0;
    const coverageTrendPercent = historicalDailySales > 0
      ? Math.round(((forecastDailySalesCoverage - historicalDailySales) / historicalDailySales) * 100)
      : 0;

    const arrivalDate = new Date(reorderDate);
    arrivalDate.setDate(arrivalDate.getDate() + vendorLeadTime);

    const nextCycleEnd = new Date(arrivalDate);
    nextCycleEnd.setDate(nextCycleEnd.getDate() + coverageDays);

    const daysToReorder = Math.max(0, Math.ceil((reorderDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)));

    return {
      currentStock,
      incomingQuantity,
      safetyStock,
      suggestedQuantity,
      vendorLeadTime,
      coverageDays,
      historicalDailySales,
      forecastDailySalesLeadTime,
      forecastDailySalesCoverage,
      forecastLeadTimeDemand,
      forecastNextCycleDemand,
      inventoryAtArrival,
      inventoryAfterRestock,
      inventoryAtNextReorder,
      isAboveSafetyAtArrival,
      isAboveSafetyAtNextReorder,
      willStockOutDuringLeadTime,
      daysUntilStockout,
      daysRemaining: daysUntilStockout,
      leadTimeTrendPercent,
      coverageTrendPercent,
      daysToReorder,
      reorderPoint,
      inventoryAtReorder,
      forecastDemandToReorder,
      todayFormatted: today.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      reorderDateFormatted: reorderDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      arrivalDateFormatted: arrivalDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      nextCycleEndFormatted: nextCycleEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      totalInventoryNeed: forecastNextCycleDemand + safetyStock,
      rawSuggestedQuantity: forecastNextCycleDemand + safetyStock - incomingQuantity,
    };
  }
}
